// The requisition screens' extension slots: where other lanes mount into lane B2's requisition detail, its timeline
// and the Requisitions list without editing them (lane contract C-B10-13, B3 FU-TAB, B8 S12, B9 S11/S12, T-12). Each
// slot is a small ordered registry filled at import from the mounting lane's own `views/<lane>/register.ts`; the
// screens read it at render. Labels and headers are his words, passed in from the mounting lane's labels module.
//
//   tabs          RequisitionDetail.tsx `registerRequisitionTab` (it also registers the tab's route)
//   banners       RequisitionDetail.tsx `registerRequisitionBanner`
//   timelineBody  registerTimelineBody(def)        one occupant: replaces the Requisition timeline tab's History body
//                                                  (B10 S18's spine timeline); a second registration throws
//   listColumns   registerRequisitionColumn(def)   a column of the Requisitions list (its table, and a fact on its cards)
//   listSignals   registerListSignals(def)         one occupant: the list's one batched read of its rows' signals, handed
//                                                  to every column's cell (B10 S19's Req health and Search Readiness)
//   savedViews    registerSavedViews(def)          one occupant: the saved-views control in the Requisitions header
//
// The Mission Control list (RequisitionsList.tsx, D-11) reads all three; V1's list (Requisitions.tsx) reads the columns
// and the saved views.
//
// A duplicate key throws at import, naming both lanes, so a clash fails the first test that loads the screen.

import type { ComponentType, ReactNode } from "react";
import type { RequisitionFull, RequisitionListOutput, SignalRow } from "@/api/tools";

export interface SlotEntry {
  readonly key: string;
  readonly lane: string;
  /** Lower first; equal order keeps registration order. Default 50. */
  readonly order?: number | undefined;
}

export interface Slot<T extends SlotEntry> {
  register(entry: T): void;
  /** The entries, ordered by `order` then registration. */
  list(): readonly T[];
}

/** An ordered registry with unique keys; `single` admits one entry only. */
export function createSlot<T extends SlotEntry>(name: string, opts: { single?: boolean; reserved?: readonly string[] } = {}): Slot<T> {
  const entries: T[] = [];
  return {
    register(entry) {
      if (opts.reserved?.includes(entry.key)) throw new Error(`${name}: "${entry.key}" is reserved (${entry.lane})`);
      const first = opts.single ? entries[0] : entries.find((e) => e.key === entry.key);
      if (first) throw new Error(`${name}: "${entry.key}" is registered twice (${first.lane} and ${entry.lane})`);
      entries.push(entry);
    },
    list() {
      return entries
        .map((entry, at) => ({ entry, at }))
        .sort((a, b) => (a.entry.order ?? 50) - (b.entry.order ?? 50) || a.at - b.at)
        .map(({ entry }) => entry);
    },
  };
}

// ---- the requisition detail ------------------------------------------------------------------------------------------
export interface RequisitionTabProps {
  r: RequisitionFull;
  timeZone: string;
  /** The route parts after the tab key (`#requisitions/R-1040/offer/A-7` → ["A-7"]); empty unless `deep`. */
  rest: readonly string[];
}

export interface RequisitionTabDef extends SlotEntry {
  /** The route segment and the tab's key: `#requisitions/R-####/<key>`. "" and "timeline" are B2's own. */
  readonly key: string;
  /** His word for the tab, from the mounting lane's labels module. */
  readonly label: string;
  readonly component: ComponentType<RequisitionTabProps>;
  /** Whether this viewer sees the tab (a manifest tool, never a role); default always. */
  readonly visible?: ((can: (tool: string) => boolean, r: RequisitionFull) => boolean) | undefined;
  /** Also claim the routes under the tab (`<key>/…`); default only `<key>` itself. */
  readonly deep?: boolean | undefined;
}

export interface RequisitionBannerProps {
  r: RequisitionFull;
  timeZone: string;
  can: (tool: string) => boolean;
}

export interface RequisitionBannerDef extends SlotEntry {
  /** Renders null when it has nothing to say for this requisition. */
  readonly component: ComponentType<RequisitionBannerProps>;
}

/** The Intake & Panel and Candidates tabs are V1 links a lane may replace; the Requisition and timeline tabs are not. */
export const RESERVED_TAB_KEYS = ["", "timeline"] as const;
export const requisitionTabs = createSlot<RequisitionTabDef>("requisition tab", { reserved: RESERVED_TAB_KEYS });
export const requisitionBanners = createSlot<RequisitionBannerDef>("requisition banner");

// ---- the Requisition timeline tab (C-B10-13) -------------------------------------------------------------------------
export interface TimelineBodyProps {
  r: RequisitionFull;
  timeZone: string;
  /** The Activity link where the viewer may read the audit (`audit.query`). */
  auditHref?: string | undefined;
}

export interface TimelineBodyDef extends SlotEntry {
  readonly component: ComponentType<TimelineBodyProps>;
}

export const timelineBody = createSlot<TimelineBodyDef>("timeline body", { single: true });
export const registerTimelineBody = (def: TimelineBodyDef): void => timelineBody.register(def);

// ---- the Requisitions list (C-B10-13) --------------------------------------------------------------------------------
export type RequisitionListRow = RequisitionListOutput["items"][number];

/** What the Mission Control list hands a cell besides its row (V1's list passes nothing). */
export interface RequisitionCellContext {
  /** The row's signal from the list's one batched read (absent: not yet read, or the reader may not open it). */
  readonly signal?: SignalRow | undefined;
  /** The batched read is in flight: draw the cell's skeleton. */
  readonly loading: boolean;
  /** false where the cell sits inside a card that is itself a link: words, never a link inside a link. */
  readonly links: boolean;
}

export interface RequisitionColumnDef extends SlotEntry {
  /** His word for the column header. */
  readonly header: string;
  /** The cell for one row the viewer may read in full (the panelist projection has no table). */
  readonly cell: (row: RequisitionListRow, context?: RequisitionCellContext) => ReactNode;
  /** Whether this viewer sees the column (a manifest tool); default always. */
  readonly visible?: ((can: (tool: string) => boolean) => boolean) | undefined;
}

export const requisitionColumns = createSlot<RequisitionColumnDef>("requisition list column");
export const registerRequisitionColumn = (def: RequisitionColumnDef): void => requisitionColumns.register(def);

/** The listed rows' signals, read once for the whole list. */
export interface ListSignals {
  readonly signals: ReadonlyMap<string, SignalRow>;
  /** The reader holds the read's tool. */
  readonly allowed: boolean;
  readonly loading: boolean;
}

export interface ListSignalsDef extends SlotEntry {
  /** A hook: the list calls it once per render with the ids of the rows it lists. */
  readonly useSignals: (ids: readonly string[]) => ListSignals;
}

export const listSignals = createSlot<ListSignalsDef>("list signals", { single: true });
export const registerListSignals = (def: ListSignalsDef): void => listSignals.register(def);

export interface SavedViewsProps {
  /** The list's current query flags (filters, sort, view). */
  query: URLSearchParams;
  /** Applies a saved view: the full query string to land on (no leading `?`; "" clears every flag). */
  apply: (query: string) => void;
}

export interface SavedViewsDef extends SlotEntry {
  readonly component: ComponentType<SavedViewsProps>;
}

export const savedViews = createSlot<SavedViewsDef>("saved views", { single: true });
export const registerSavedViews = (def: SavedViewsDef): void => savedViews.register(def);
